import type { CommissionTiersData, TierPercents } from '~/types/commissionTiers';
import { isCommissionTiersData } from '~/utils/tiers/ladderView';

const COMMISSION_TIERS_MAP_KEY = 'commission-tiers-map';
// Last good response — served when the endpoint fails so the ladder never falls back to hard-coded numbers.
const COMMISSION_TIERS_CACHE_KEY = 'insiders.commissionTiers.v1';

const readCachedTiers = (): CommissionTiersData | null => {
  try {
    const raw = localStorage.getItem(COMMISSION_TIERS_CACHE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return isCommissionTiersData(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const writeCachedTiers = (data: CommissionTiersData) => {
  try {
    localStorage.setItem(COMMISSION_TIERS_CACHE_KEY, JSON.stringify(data));
  } catch {
    // storage blocked — the live response still renders
  }
};

const fetchCommissionTiers = async (): Promise<CommissionTiersData | null> => {
  try {
    const res = await $fetch<{ success?: boolean; data?: unknown }>('/api/agent/commission-tiers');
    if (!isCommissionTiersData(res?.data)) return readCachedTiers();
    writeCachedTiers(res.data);
    return res.data;
  } catch {
    return readCachedTiers();
  }
};

export function useCommissionTiersMap() {
  const route = useRoute();

  const { data, status, pending, refresh } = useAsyncData(COMMISSION_TIERS_MAP_KEY, fetchCommissionTiers, {
    server: false,
    lazy: false,
    default: () => null,
    watch: [() => route.fullPath],
  });

  const map = computed<Record<string, TierPercents>>(() => data.value?.map ?? {});
  const ladder = computed(() => data.value?.ladder ?? []);
  const maxSelfClosePercent = computed(
    () => data.value?.maxSelfClosePercent ?? Math.max(0, ...ladder.value.map((r) => r.selfClosePercent)),
  );
  const maxReferralPercent = computed(
    () => data.value?.maxReferralPercent ?? Math.max(0, ...ladder.value.map((r) => r.referralPercent)),
  );
  const referralCapPercent = computed(() => data.value?.referralCapPercent ?? null);
  // Settled with nothing to show: the endpoint failed and there is no cached response.
  const isUnavailable = computed(() => (status.value === 'success' || status.value === 'error') && !data.value);

  // A UAE Legend reads the Elite row; LEGEND has no row of its own.
  const rowKey = (tier: string | undefined) => (tier === 'LEGEND' ? 'ELITE' : tier || 'STARTER');

  const getSelfClose = (tier: string | undefined) => {
    const k = rowKey(tier);
    return map.value[k]?.selfClosePercent ?? 50;
  };

  const getReferral = (tier: string | undefined) => {
    const k = rowKey(tier);
    return map.value[k]?.referralPercent ?? 30;
  };

  return {
    data,
    map,
    ladder,
    maxSelfClosePercent,
    maxReferralPercent,
    referralCapPercent,
    isUnavailable,
    pending,
    refresh,
    getSelfClose,
    getReferral,
  };
}

export { COMMISSION_TIERS_MAP_KEY };
